import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import MarkdownPreview from '@uiw/react-markdown-preview';

import Navigation from '../navigation/Navigation';

import { STAR_COUNT, bookmarkMessage, createStarMessages, getChatMessages, getLocal, getMessage, getStarMessages, sendAction, sendChatMessage, unbookmarkMessage } from '../../utils/api';

import './Guide.css';

function Guide({ handleError, handleFeedback }) {
  const { starId, tileId } = useParams();

  const [isCommenting, setIsCommenting] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [message, setMessage] = useState('');
  const [comment, setComment] = useState('');
  const [commentVerb, setCommentVerb] = useState('');
  const [activeMsg, setActiveMsg] = useState('');
  const [messages, setMessages] = useState([]);
  const [name, setName] = useState(null);
  const [stars, setStars] = useState(null);
  const [scrollToResponse, setScrollToResponse] = useState(false);

  const getMessages = useCallback(async () => {
    let newMessages = [];

    try {
      newMessages = await getChatMessages();
    } catch (e) {
      handleError(e);
    }

    setMessages(newMessages);
  }, [handleError]);

  const getStars = useCallback(async () => {
    let newStars = [];

    try {
      newStars = await getStarMessages();

      if (!newStars?.length) {
        newStars = [];
      }

      if (newStars.length < STAR_COUNT) {
        const additionalStars = await createStarMessages(STAR_COUNT - newStars.length);

        if (additionalStars?.length) {
          newStars = [...newStars, ...additionalStars];
        }
      }
    } catch (e) {
      handleError(e);
    }

    setStars(newStars);
  }, [handleError]);

  const postMessage = useCallback(async (message, parentMessageId = null) => {
    setMessage('');

    setIsSending(message);
    
    try {
      const response = await sendChatMessage(message, parentMessageId);

      if (response !== null) {
        setIsSending(false);
        setScrollToResponse(true);
        setMessages(prevMessages => [...prevMessages, response.message]);
      }
    } catch (e) {
      setMessage(message);
      setIsSending(false);
      handleError(e);
    }

  }, [handleError]);

  const resetCommentState = () => {
    setIsCommenting(false);
    setActiveMsg('');
    setComment('');
    setCommentVerb('');
  };

  const postComment = useCallback(async (comment, messageId) => {
    let metaData = {};
    try {

      metaData.comment = comment;      
      const response = await sendAction('comment', messageId, metaData);

      if (response !== null) {
        resetCommentState();
      }
    } catch (e) {
      handleError(e);
    }

  }, [handleError]);

  const handleInput = useCallback((e) => {
    setMessage(e.target.value);
  }, []);

  const handleKey = useCallback(async (e) => {
    const message = e.target.value;

    if (e.keyCode === 13 && message.length) {
      await postMessage(message);
    }
  }, [postMessage]);

  const handleBookmark = useCallback(async messageIndex => {
    try {
      const message = messages[messageIndex];

      if (message.bookmarkActionId) {
        const response = await unbookmarkMessage(message.bookmarkActionId);

        if (response) {
          setMessages(prevMessages => {
            prevMessages[messageIndex].bookmarkActionId = undefined;
            return [...prevMessages];
          });
        }
      } else {
        const response = await bookmarkMessage(message._id);

        if (response !== null) {
          setMessages(prevMessages => {
            prevMessages[messageIndex].bookmarkActionId = response;
            return [...prevMessages];
          });
        }
      }
    } catch (e) {
      handleError(e);
    }
  }, [handleError, messages]);

  const handleCommentInput = useCallback((e) => {
    setComment(e.target.value);
  }, []);

  const handleCommentKey = useCallback(async (e) => {
    const comment = e.target.value;

    if (e.keyCode === 13 && comment.length) {
      await postComment(comment, activeMsg);
    }
  }, [postComment, activeMsg]);

  const handleDislike = useCallback(async message => {
    try {
      const response = await sendAction('dislike', message._id);

      if (response !== null) {
        setCommentVerb('not like');
        setIsCommenting(true);
        setActiveMsg(message._id);
      }
    } catch (e) {
      handleError(e);
    }
  }, [handleError]);

  const handleLike = useCallback(async message => {
    try {
      const response = await sendAction('like', message._id);

      if (response !== null) {
        setCommentVerb('like');
        setIsCommenting(true);
        setActiveMsg(message._id);
      }
    } catch (e) {
      handleError(e);
    }
  }, [handleError]);

  useEffect(() => {
    const handleStar = async () => {
      if (starId) {
        const stars = getLocal('stars');
        
        if (stars?.length) {
          const star = stars.find(star => star.index === parseInt(starId, 10));

          if (star?.summary) {
            await postMessage('Please tell me more about "' + star.summary + '"');
          }
        }
      }
    };

    const handleTile = async () => {
      if (tileId) {
        const tile = await getMessage(tileId);

        if (tile?.prompt_for_more_info) {
          await postMessage(tile.prompt_for_more_info, tileId);
        }
      }
    };

    getStars();
    getMessages();
    handleStar();
    handleTile();
  }, [getMessages, getStars, postMessage, starId, tileId]);

  useEffect(() => {
    if (scrollToResponse) {
      const responses = document.querySelectorAll('#guide .chat .response');

      if (responses.length) {
        const response = responses[responses.length - 1];

        if (response) {
          response.scrollIntoView();
        }
      }

      setScrollToResponse(false);
    } else {
      const chat = document.querySelector('#guide .chat');

      if (chat) {
        chat.scrollTop = chat.scrollHeight;
      }
    }

    const profile = getLocal('profile');

    if (profile?.meta_data?.length) {
      const param = profile.meta_data.find(p => p.param === 'name');

      if (param?.value) {
        setName(param.value);
      }
    }
  }, [isSending, messages]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        resetCommentState();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  return (
    <div id="content">
      <div id="guide">
        <div className="scrollable chat">
          <div className="suggestions">
            <p>Hi{name ? ' ' + name : ''}, I’m Lexa, an AI Guide who can help your CSF Advisor help you. Based on what you told me, here are some things that you might want to explore in more detail, or I can answer any questions you have.</p>
            {stars === null ? (
              <p>Give me a sec...</p>
            ) : null}
            {stars?.length ? (
              <ul>
                {stars.map((star, i) => (
                  <li key={i}>
                    <span onClick={() => postMessage(star.prompt_for_more_info, star._id)}>{star.title}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
          {messages.map((message, i) => 
            <React.Fragment key={i}>
              <div className="request">{message.message}</div>
              <div className="response">
                <MarkdownPreview source={message.response} components={{ a: ({ children, href, ...markdownProps }) => (
                  <a href={href} target="_blank" rel="noopener noreferrer" {...markdownProps}>{children}</a>
                )}} />
                <div className="controls">
                  <div className="left"></div>
                  <div className="center">
                    <p>Please rate this response</p>
                    <div>
                      <img src={process.env.PUBLIC_URL + '/assets/icons/dislike.svg'} alt="Dislike" onClick={() => handleDislike(message)} />
                      <img src={process.env.PUBLIC_URL + '/assets/icons/like.svg'} alt="Like" onClick={() => handleLike(message)} />
                    </div>
                  </div>
                  <div className="right">
                    <img src={process.env.PUBLIC_URL + '/assets/icons/bookmark' + (message.bookmarkActionId ? '-active' : '') + '.svg'} alt="Bookmark" onClick={() => handleBookmark(i)} />
                  </div>
                </div>
              </div>
              {message.topics?.length ? (
                <div className="suggestions">
                  <div className="title">
                    <img src={process.env.PUBLIC_URL + '/assets/icons/guide-white.svg'} alt="Suggestions" />
                    <span>Suggestions</span>
                  </div>
                  <ul>
                    {message.topics.map((topic, i) => (
                      <li key={i}>
                        <span onClick={() => postMessage(topic.question, message._id)}>{topic.topic}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </React.Fragment>
          )}
          {isSending ? (
            <>
              <div className="request">{isSending}</div>
              <div className="response"><p>Give me a sec...</p></div>
            </>
          ) : null}
        </div>
        <div className="text-input">
          {/* <img className="icon-chat" src={process.env.PUBLIC_URL + '/assets/icons/guide.svg'} alt="Chat" /> */}
          <input type="input" value={message} name="chat" placeholder={!!isSending ? 'Give me a sec...' : 'Ask me anything!'} autoComplete="off" disabled={!!isSending} onChange={(e) => handleInput(e)} onKeyDown={(e) => handleKey(e)} />
          <img className="icon-submit" src={process.env.PUBLIC_URL + '/assets/icons/submit.svg'} alt="Submit" onClick={() => postMessage(message)} />
        </div>
      </div>
      <Navigation handleFeedback={handleFeedback} />
      {isCommenting ? (
        <div id="comment-container" className="popup-overlay" onClick={(e) => { if (e.target.id === 'comment-container') resetCommentState(); }} >
          <div className="popup" onClick={(e) => e.stopPropagation()}>
            <div className="text-input">
              {/* <img className="icon-chat" src={process.env.PUBLIC_URL + '/assets/icons/guide.svg'} alt="Chat" /> */}
              <input type="input" value={comment} name="comment" placeholder={!!isSending ? 'Give me a sec...' : `Why did you ${commentVerb} this response?`} autoComplete="off" disabled={!!isSending} onChange={(e) => handleCommentInput(e)} onKeyDown={(e) => handleCommentKey(e)} />
              <img className="icon-submit" src={process.env.PUBLIC_URL + '/assets/icons/submit.svg'} alt="Submit" onClick={() => {postComment(comment, activeMsg)}} />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default withAuthenticationRequired(Guide);